import type { RuleGroupType, RuleType } from "react-querybuilder";
import {
  buildConditionalPlansQuery,
  fetchConditionalPlans,
  normalizePlanTypeApiCode,
  type AutoTaggingPlanDimensionItem,
  type ConditionalPlanRecord,
} from "../../../services/autoTaggingService";
import { buildReferenceItemsFromObjectSubType } from "../TaggingPlan/classificationPlanApiMapping";
import type { ReferenceDimensionSelection } from "../TaggingPlan/ClassificationCascadePicker";
import {
  countNumberFromRuleValue,
  isCountRule,
  normalizeCountField,
  canonicalCountOperator,
} from "./countCondition";
import { parseEnhancedTaggingConditionValue } from "./enhancedTaggingConditionValue";
import type { EnhancedTaggingThenTag } from "./EnhancedTaggingRuleThenStep";

function isRuleGroup(item: unknown): item is RuleGroupType {
  return Boolean(item && typeof item === "object" && "rules" in item);
}

/** Normalize leaf values to sorted codes so name-only differences do not matter. */
function normalizeValueCodes(value: unknown): string[] {
  const parsed = parseEnhancedTaggingConditionValue(value);
  if (parsed.allValues) return ["*"];
  return parsed.conceptValues
    .map((item) => item.code.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

function normalizeConditionForCompare(condition: unknown): unknown {
  if (!isRuleGroup(condition)) return null;
  return {
    combinator: String(condition.combinator ?? "and").toLowerCase(),
    not: Boolean(condition.not),
    rules: (condition.rules ?? []).map((item) => {
      if (isRuleGroup(item)) return normalizeConditionForCompare(item);
      const rule = item as RuleType;
      if (isCountRule(rule)) {
        return {
          field: normalizeCountField(String(rule.field ?? "")),
          operator: canonicalCountOperator(String(rule.operator ?? "")),
          value: [String(countNumberFromRuleValue(rule.value))],
          ruleType: "count",
        };
      }
      return {
        field: String(rule.field ?? "").trim(),
        operator: String(rule.operator ?? "").trim(),
        value: normalizeValueCodes(rule.value),
      };
    }),
  };
}

export function conditionsAreEquivalent(
  left: unknown,
  right: unknown,
): boolean {
  try {
    return (
      JSON.stringify(normalizeConditionForCompare(left)) ===
      JSON.stringify(normalizeConditionForCompare(right))
    );
  } catch {
    return false;
  }
}

export type DuplicateConditionalPlanMatch = {
  id: string;
};

type ConditionalPlanApiErrorItem = {
  field?: unknown;
  message?: unknown;
  details?: unknown;
};

function readExistingPlanIdFromDetails(details: unknown): string {
  if (!details || typeof details !== "object") return "";
  const record = details as Record<string, unknown>;
  return String(
    record.existing_plan_id ??
      record.existingPlanId ??
      record.plan_id ??
      record.planId ??
      record.id ??
      "",
  ).trim();
}

function extractExistingPlanIdFromErrorsArray(
  errors: unknown,
): string | null {
  if (!Array.isArray(errors)) return null;

  for (const item of errors) {
    if (!item || typeof item !== "object") continue;
    const errorItem = item as ConditionalPlanApiErrorItem;
    const existingId = readExistingPlanIdFromDetails(errorItem.details);
    if (!existingId) continue;
    const message = String(errorItem.message ?? "");
    const field = String(errorItem.field ?? "");
    // Duplicate conflict: condition field and/or "already exists" message.
    if (field === "condition" || messageLooksLikeDuplicate(message)) {
      return existingId;
    }
  }

  for (const item of errors) {
    if (!item || typeof item !== "object") continue;
    const existingId = readExistingPlanIdFromDetails(
      (item as ConditionalPlanApiErrorItem).details,
    );
    if (existingId) return existingId;
  }

  return null;
}

/**
 * Prefer structured API payload. Real backend shape nests under `data`:
 * `{ status, code, message, data: { errors: [{ details: { existing_plan_id } }] } }`
 * Also accepts a flat `{ errors: [...] }` body.
 */
export function extractExistingPlanIdFromApiPayload(
  payload: unknown,
): string | null {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;

  const fromTopLevel = extractExistingPlanIdFromErrorsArray(record.errors);
  if (fromTopLevel) return fromTopLevel;

  const data = record.data;
  if (data && typeof data === "object") {
    return extractExistingPlanIdFromErrorsArray(
      (data as Record<string, unknown>).errors,
    );
  }

  return null;
}

/**
 * Parse create/update API errors that indicate a duplicate conditional plan.
 * Prefer `errors[].details.existing_plan_id` from the API body.
 */
export function parseDuplicateConditionalPlanError(
  error: unknown,
): DuplicateConditionalPlanMatch | null {
  const responseData =
    error && typeof error === "object" && "responseData" in error
      ? (error as { responseData?: unknown }).responseData
      : null;

  const fromPayload = extractExistingPlanIdFromApiPayload(responseData);
  if (fromPayload) return { id: fromPayload };

  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  const trimmed = message.trim();
  if (!trimmed) return null;
  if (!messageLooksLikeDuplicate(trimmed)) {
    return null;
  }

  const id = extractRuleIdFromMessage(trimmed);
  return { id: id || "existing" };
}

const DUPLICATE_MESSAGE_MARKERS = [
  "already exists",
  "duplicate",
  "same condition",
  "same conditions",
  "output dimension already exists",
] as const;

function messageLooksLikeDuplicate(message: string): boolean {
  const lower = message.toLowerCase();
  return DUPLICATE_MESSAGE_MARKERS.some((marker) => lower.includes(marker));
}

function isRuleIdChar(ch: string | undefined): boolean {
  if (!ch) return false;
  const code = ch.charCodeAt(0);
  return (
    (code >= 48 && code <= 57) ||
    (code >= 65 && code <= 90) ||
    (code >= 97 && code <= 122) ||
    ch === "_" ||
    ch === "-"
  );
}

function isWhitespaceChar(ch: string | undefined): boolean {
  return ch === " " || ch === "\t" || ch === "\n" || ch === "\r";
}

/**
 * Parse rule ids from human-readable API messages without RegExp
 * (avoids Sonar regex security hotspots).
 * Supports: "(Rule ENH1)", "Rule #ENH1", "Rule ENH1", bare "ENH123".
 */
function extractRuleIdFromMessage(message: string): string | null {
  const lower = message.toLowerCase();

  const parenIdx = lower.lastIndexOf("(rule");
  if (parenIdx !== -1) {
    const id = readRuleIdToken(message, parenIdx + "(rule".length);
    if (id) return id;
  }

  let searchFrom = 0;
  while (searchFrom < lower.length) {
    const ruleIdx = lower.indexOf("rule", searchFrom);
    if (ruleIdx === -1) break;
    const id = readRuleIdToken(message, ruleIdx + "rule".length);
    if (id) return id;
    searchFrom = ruleIdx + 4;
  }

  const upper = message.toUpperCase();
  let enhFrom = 0;
  while (enhFrom < upper.length) {
    const enhIdx = upper.indexOf("ENH", enhFrom);
    if (enhIdx === -1) break;
    let end = enhIdx + 3;
    while (end < upper.length) {
      const code = upper.charCodeAt(end);
      if (code < 48 || code > 57) break;
      end += 1;
    }
    if (end > enhIdx + 3) return message.slice(enhIdx, end);
    enhFrom = enhIdx + 3;
  }

  return null;
}

function readRuleIdToken(message: string, fromIndex: number): string | null {
  let i = fromIndex;
  while (isWhitespaceChar(message[i])) i += 1;
  if (message[i] === "#") {
    i += 1;
    while (isWhitespaceChar(message[i])) i += 1;
  }
  const start = i;
  while (isRuleIdChar(message[i])) i += 1;
  if (i === start) return null;
  return message.slice(start, i);
}

/**
 * Find an existing conditional plan with the same object scope, tag, and
 * conditions. Excludes `excludeRuleId` (edit mode self-match).
 */
function canonicalReferenceItems(
  items: readonly AutoTaggingPlanDimensionItem[],
): string {
  return JSON.stringify(
    items.map((item) => ({
      concept: item.concept.trim().toLowerCase(),
      all_values: item.all_values,
      values: item.all_values
        ? []
        : item.values
            .map((value) => value.code.trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b)),
    })),
  );
}

export async function findDuplicateConditionalPlan(options: {
  objectType: string;
  objectSubType: string;
  objectSubTypeName?: string;
  referenceConceptCode?: string;
  referenceDimensions?: readonly ReferenceDimensionSelection[];
  conditionQuery: RuleGroupType;
  thenTag: EnhancedTaggingThenTag;
  excludeRuleId?: string;
}): Promise<DuplicateConditionalPlanMatch | null> {
  const targetType = normalizePlanTypeApiCode(options.objectType);
  const concept = options.thenTag.dimensionCode.trim();
  const valueCode = options.thenTag.valueCode.trim();
  if (!targetType || !concept || !valueCode) return null;

  const draftReferenceItems = buildReferenceItemsFromObjectSubType({
    objectSubType: options.objectSubType,
    objectSubTypeName: options.objectSubTypeName,
    referenceConceptCode: options.referenceConceptCode,
    referenceDimensions: options.referenceDimensions,
  });
  const draftReferenceKey = canonicalReferenceItems(draftReferenceItems);

  const query = buildConditionalPlansQuery({
    objectType: targetType,
    concept,
    conceptValueCode: valueCode,
    page: 1,
    pageSize: 100,
  });

  const { items } = await fetchConditionalPlans(query);
  const excludeId = options.excludeRuleId?.trim() ?? "";

  const match = items.find((plan: ConditionalPlanRecord) => {
    if (excludeId && plan.id === excludeId) return false;
    if (normalizePlanTypeApiCode(plan.target_type) !== targetType) return false;
    if (canonicalReferenceItems(plan.reference_items) !== draftReferenceKey) {
      return false;
    }
    if (plan.concept.trim() !== concept) return false;
    if (plan.concept_value.code.trim() !== valueCode) return false;
    return conditionsAreEquivalent(plan.condition, options.conditionQuery);
  });

  return match ? { id: match.id } : null;
}
